import type { ComponentProps } from 'react';
import { memo, Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import defaultAvatar from '@/assets/img/default-avatar.png';
import { useGetDuoSummary } from '@/features/profile/hooks/useGetDuoSummary';
import { MutualDuosModal } from '@/features/profile/modals/mutualDuosModal';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/utils/cn';

export interface ProfileDuoCountsProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  userId: string;
}

export const ProfileDuoCounts = memo(function ProfileDuoCounts(
  props: ProfileDuoCountsProps,
) {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<ProfileDuoCountsSkeleton />}>
        <ProfileDuoCountsContent {...props} />
      </Suspense>
    </ErrorBoundary>
  );
});

function ProfileDuoCountsContent({
  userId,
  className,
  ...props
}: ProfileDuoCountsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: summary } = useGetDuoSummary({ userId });

  if (summary.totalCount === 0) {
    return null;
  }

  const mutualPreviews = summary.mutualDuos.preview;

  return (
    <>
      <div
        className={cn('flex items-center gap-2 text-xs', className)}
        {...props}
      >
        <span>
          <span className="text-cold-100 font-bold">{summary.totalCount}</span>{' '}
          <span className="text-cold-400">
            {summary.totalCount === 1 ? 'Duo' : 'Duos'}
          </span>
        </span>

        {mutualPreviews.length > 0 && (
          <>
            <div className="bg-cold-600 size-1 shrink-0 rounded-full" />

            <button
              type="button"
              className="flex cursor-pointer items-center gap-1"
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              <div className="flex items-center -space-x-3">
                {mutualPreviews.slice(0, 3).map(duo => (
                  <Avatar
                    key={duo.userId}
                    className="border-cold-950 size-5 border"
                  >
                    <AvatarFallback>
                      <img
                        src={defaultAvatar}
                        alt="Default avatar"
                        className="size-full object-cover"
                      />
                    </AvatarFallback>
                    <AvatarImage src={duo.pictureUrl} alt="Mutual duo" />
                  </Avatar>
                ))}
              </div>
              {summary.mutualDuos.count >= 3 && (
                <span className="text-cold-100 font-bold">
                  {summary.mutualDuos.count}
                </span>
              )}
              <span className="text-cold-400">Mutual</span>
            </button>
          </>
        )}
      </div>
      <MutualDuosModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        userId={userId}
        mutualCount={summary.mutualDuos.count}
      />
    </>
  );
}

function ProfileDuoCountsSkeleton() {
  return (
    <div className="flex items-center gap-2">
      <Skeleton className="h-4 w-12" />
      <Skeleton className="size-1 rounded-full" />
      <div className="flex items-center">
        <Skeleton className="size-5 rounded-full" />
        <Skeleton className="-ml-2 size-5 rounded-full" />
        <Skeleton className="-ml-2 size-5 rounded-full" />
      </div>
      <Skeleton className="h-4 w-14" />
    </div>
  );
}
